import {useCallback, useMemo} from 'react';
import {useSearchParams} from 'react-router';

const FOLDER_PATH_PARAM = 'folderPath';

/**
 * Parse folder path from URL search params
 * @param {string|null} rawPath - Raw path string from URL
 * @returns {string[]} Parsed folder path array
 */
const parseFolderPath = (rawPath) => {
    if (!rawPath) return [];
    try {
        const parsed = JSON.parse(rawPath);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Serialize folder path for URL
 * @param {string[]} path - Folder path array
 * @returns {string|null} JSON string or null if empty
 */
const serializeFolderPath = (path) => {
    if (!path || path.length === 0) return null;
    return JSON.stringify(path);
};

/**
 * Hook to manage folder navigation via URL search params.
 * Consolidates useFolderPath and useFolderPathNav into a single hook.
 *
 * @returns {Object} Navigation state and methods
 */
export const useFolderNavigation = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const path = useMemo(
        () => parseFolderPath(searchParams.get(FOLDER_PATH_PARAM)),
        [searchParams]
    );

    const currentFolderId = path.length > 0 ? path[path.length - 1] : null;
    const isRootLevel = currentFolderId === null;

    const updatePath = useCallback((nextPath) => {
        setSearchParams(() => {
            // Use actual window.location.search as base to preserve params written
            // directly via replaceState (e.g. isBuilderOpen from the store).
            const next = new URLSearchParams(window.location.search);
            const serialized = serializeFolderPath(nextPath);

            if (serialized) {
                next.set(FOLDER_PATH_PARAM, serialized);
            } else {
                next.delete(FOLDER_PATH_PARAM);
            }

            return next;
        });
        // }, {replace: true});
    }, [setSearchParams]);

    const push = useCallback((folderId) => {
        updatePath([...path, folderId]);
    }, [path, updatePath]);

    const pop = useCallback(() => {
        if (path.length === 0) return;
        updatePath(path.slice(0, -1));
    }, [path, updatePath]);

    const reset = useCallback(() => {
        updatePath([]);
    }, [updatePath]);

    return {
        path,
        currentFolderId,
        isRootLevel,
        push,
        pop,
        reset,
        setPath: updatePath,
    };
};
